import { Component, OnInit, AfterViewInit, TemplateRef, ViewChild, Input } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';

import { AlertService, DialogType, MessageSeverity } from '../../services/alert.service';
import { AppTranslationService } from '../../services/app-translation.service';
import { AccountService } from '../../services/account.service';
import { Utilities } from '../../services/utilities';
import { User } from '../../models/user.model';
import { Role } from '../../models/role.model';
import { Permission } from '../../models/permission.model';
import { UserEdit } from '../../models/user-edit.model';
import { UserInfoComponent } from './user-info.component';
import { AuthService } from '../../services/auth.service';
import { ConfigurationService } from '../../services/configuration.service';
import Swal from 'sweetalert2';


@Component({
    selector: 'app-users-management',
    templateUrl: './users-management.component.html',
    styleUrls: ['./users-management.component.scss'],
    standalone: false
})
export class UsersManagementComponent implements OnInit, AfterViewInit {
  columns: any[] = [];
  rows: User[] = [];
  rowsCache: User[] = [];
  editedUser: UserEdit;
  sourceUser: UserEdit;
  editingUserName: { name: string };
  loadingIndicator: boolean;

  allRoles: Role[] = [];
  showAllUsers: boolean = false;


  @ViewChild('indexTemplate', { static: true })
  indexTemplate: TemplateRef<any>;

  @ViewChild('userNameTemplate', { static: true })
  userNameTemplate: TemplateRef<any>;

  @ViewChild('rolesTemplate', { static: true })
  rolesTemplate: TemplateRef<any>;

  @ViewChild('statusTemplate', { static: true })
  statusTemplate: TemplateRef<any>;

  @ViewChild('actionsTemplate', { static: true })
  actionsTemplate: TemplateRef<any>;

  @ViewChild('editorModal', { static: true })
  editorModal: ModalDirective;

  @ViewChild('userEditor', { static: true })
  userEditor: UserInfoComponent;

  constructor(private alertService: AlertService, private translationService: AppTranslationService, private accountService: AccountService,
    private authService: AuthService, private configurations: ConfigurationService) {
  }


  ngOnInit() {

    const gT = (key: string) => this.translationService.getTranslation(key);

    this.columns = [
      { prop: 'index', name: '#', width: 40, cellTemplate: this.indexTemplate, canAutoResize: false },
      { prop: 'jobTitle', name: gT('users.management.Title'), width: 70 },
      { prop: 'userName', name: gT('users.management.UserName'), width: 90, cellTemplate: this.userNameTemplate },
      { prop: 'fullName', name: gT('users.management.FullName'), width: 120 },
      { prop: 'email', name: gT('users.management.Email'), width: 140 },
      
      { prop: 'Active', name: gT('Status') || 'Status', width: 90, cellTemplate: this.statusTemplate },
      
      { prop: 'sfid', name: gT('users.management.SFID'), width: 120 },
      { prop: 'roles', name: gT('users.management.Roles'), width: 80, cellTemplate: this.rolesTemplate },
      //{ prop: 'phoneNumber', name: gT('users.management.PhoneNumber'), width: 100 }

    ];

    if (this.canManageUsers) {
      this.columns.push({ name: '', width: 340, cellTemplate: this.actionsTemplate, resizeable: false, canAutoResize: false, sortable: false, draggable: false });
    }

    this.loadData();
  }

  onShowAllChanged(event: any) {
    this.showAllUsers = !!event.target.checked;
    this.applyShowAllFilter();
  }

  private applyShowAllFilter() {
    if (!this.rowsCache) {
      this.rows = [];
      return;
    }

    if (this.showAllUsers) {
      this.rows = [...this.rowsCache];
    } else {
      // show only enabled/active users by default
      this.rows = this.rowsCache.filter(r => !!r.isEnabled);
    }

    // reassign indexes
    this.rows.forEach((user, index) => (user as any).index = index + 1);
  }


  ngAfterViewInit() {

    this.userEditor.changesSavedCallback = () => {
      this.addNewUserToList();
      this.editorModal.hide();
    };

    this.userEditor.changesCancelledCallback = () => {
      this.editedUser = null;
      this.sourceUser = null;
      this.editorModal.hide();
    };
  }


  addNewUserToList() {
    if (this.sourceUser) {
      Object.assign(this.sourceUser, this.editedUser);

      let sourceIndex = this.rowsCache.indexOf(this.sourceUser, 0);
      if (sourceIndex > -1) {
        Utilities.moveArrayItem(this.rowsCache, sourceIndex, 0);
      }

      sourceIndex = this.rows.indexOf(this.sourceUser, 0);
      if (sourceIndex > -1) {
        Utilities.moveArrayItem(this.rows, sourceIndex, 0);
      }

      this.editedUser = null;
      this.sourceUser = null;
    } else {
      const user = new User();
      Object.assign(user, this.editedUser);
      this.editedUser = null;

      let maxIndex = 0;
      for (const u of this.rowsCache) {
        if ((u as any).index > maxIndex) {
          maxIndex = (u as any).index;
        }
      }

      (user as any).index = maxIndex + 1;

      this.rowsCache.splice(0, 0, user);
      this.rows.splice(0, 0, user);
      this.rows = [...this.rows];
    }
  }


  loadData() {
    this.alertService.startLoadingMessage();
    this.loadingIndicator = true;

    if (this.canViewRoles) {
      this.accountService.getUsersAndRoles().subscribe(results => this.onDataLoadSuccessful(results[0], results[1]), error => this.onDataLoadFailed(error));
    } else {
      this.accountService.getUsers().subscribe(users => this.onDataLoadSuccessful(users, this.accountService.currentUser.roles.map(x => new Role(x))), error => this.onDataLoadFailed(error));
    }
  }


  onDataLoadSuccessful(users: User[], roles: Role[]) {
    this.alertService.stopLoadingMessage();
    this.loadingIndicator = false;

    users.forEach((user, index) => {
      (user as any).index = index + 1;
    });

    this.rowsCache = [...users];
    // Apply default filter (show only active users) unless user toggled 'show all'
    this.applyShowAllFilter();

    this.allRoles = roles;
  }


  onDataLoadFailed(error: any) {
    this.alertService.stopLoadingMessage();
    this.loadingIndicator = false;

    this.alertService.showStickyMessage('Load Error', `Unable to retrieve users from the server.\r\nErrors: "${Utilities.getHttpResponseMessages(error)}"`,
      MessageSeverity.error, error);
  }


  onSearchChanged(value: string) {
    this.rows = this.rowsCache.filter(r => Utilities.searchArray(value, false, r.userName, r.fullName, r.email, r.phoneNumber, r.jobTitle, r.roles));
  }

  onEditorModalHidden() {
    this.editingUserName = null;
    this.userEditor.resetForm(true);
  }


  newUser() {
    this.editingUserName = null;
    this.sourceUser = null;
    this.editedUser = this.userEditor.newUser(this.allRoles);
    this.editorModal.show();
  }


  editUser(row: UserEdit) {
    this.editingUserName = { name: row.userName };
    this.sourceUser = row;
    this.editedUser = this.userEditor.editUser(row, this.allRoles);
    this.editorModal.show();
  }


  deleteUser(row: UserEdit) {
    this.alertService.showDialog('Are you sure you want to delete \"' + row.userName + '\"?', DialogType.confirm, () => this.deleteUserHelper(row));
  }


  deleteUserHelper(row: UserEdit) {

    this.alertService.startLoadingMessage('Deleting...');
    this.loadingIndicator = true;

    this.accountService.deleteUser(row)
      .subscribe(results => {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.rowsCache = this.rowsCache.filter(item => item !== row);
        this.rows = this.rows.filter(item => item !== row);
      },
        error => {
          this.alertService.stopLoadingMessage();
          this.loadingIndicator = false;

          this.alertService.showStickyMessage('Delete Error', `An error occured whilst deleting the user.\r\nError: "${Utilities.getHttpResponseMessages(error)}"`,
            MessageSeverity.error, error);
        });
  }



  resetMfa(row: UserEdit) {
    Swal.fire({
      title: 'Reset MFA?',
      text: `The authenticator secret for "${row.userName}" will be cleared. They will be prompted to set up MFA again on their next login.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Reset MFA',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed) {
        this.resetMfaHelper(row);
      }
    });
  }


  resetMfaHelper(row: UserEdit) {
    this.alertService.startLoadingMessage('Resetting MFA...');
    this.loadingIndicator = true;

    this.accountService.resetUserMfa(row.id)
      .subscribe(() => {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        (row as any).mfaEnabled = false;
        Swal.fire({
          title: 'MFA Reset',
          text: `MFA has been reset for "${row.userName}". They will be prompted to set up MFA again on their next login.`,
          icon: 'success',
          confirmButtonText: 'OK'
        });
      },
        error => {
          this.alertService.stopLoadingMessage();
          this.loadingIndicator = false;

          this.alertService.showStickyMessage('Reset MFA Error', `An error occured whilst resetting MFA.\r\nError: "${Utilities.getHttpResponseMessages(error)}"`,
            MessageSeverity.error, error);
        });
  }


  // Admins may reset MFA for other users, but not for their own account.
  isCurrentUser(row: UserEdit): boolean {
    return !!row && !!this.accountService.currentUser && row.id === this.accountService.currentUser.id;
  }


  private hasRole(roles: string[], role: string): boolean {
    return Array.isArray(roles) && roles.some(r => (r || '').toLowerCase() === role.toLowerCase());
  }

  // Only administrators may impersonate, and only non-admin, enabled, other users.
  // (The backend grant re-enforces every one of these — this just gates the UI.)
  canImpersonate(row: UserEdit): boolean {
    const me = this.accountService.currentUser;
    return !!row
      && !!me
      && this.hasRole(me.roles, 'administrator')
      && !this.isCurrentUser(row)
      && !this.hasRole(row.roles, 'administrator')
      && !!row.isEnabled
      && !this.authService.isImpersonating;
  }

  impersonate(row: UserEdit) {
    Swal.fire({
      title: 'Log in as this user?',
      text: `You will proxy into the app as "${row.userName}" with their role and access. A banner lets you return to your admin account at any time.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Log in as user',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.alertService.startLoadingMessage('Switching user...');
      this.loadingIndicator = true;

      this.authService.beginImpersonation(row.id).subscribe(
        () => {
          this.alertService.stopLoadingMessage();
          this.loadingIndicator = false;
          // Full reload so every role-gated view reflects the impersonated user.
          window.location.href = this.configurations.homeUrl || '/';
        },
        error => {
          this.alertService.stopLoadingMessage();
          this.loadingIndicator = false;
          this.alertService.showStickyMessage('Impersonation Error',
            `Unable to log in as this user.\r\nError: "${Utilities.getHttpResponseMessages(error)}"`,
            MessageSeverity.error, error);
        });
    });
  }


  get canAssignRoles() {
    return this.accountService.userHasPermission(Permission.assignRolesPermission);
  }

  get canViewRoles() {
    return this.accountService.userHasPermission(Permission.viewRolesPermission);
  }

  get canManageUsers() {
    return this.accountService.userHasPermission(Permission.manageUsersPermission);
  }
}
